import { FunctionComponent, useEffect, useState } from "react";
import classes from "./index.module.css";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import parcelsRepository from "client-sdk/src/Repository/parcels-repository";
import handleError from "../../../utils/handleError";
import handleNotfication from "../../../utils/handleNotfication";

interface CustomizedItemsCardProps {
  parcel: any;
  userRole: string;
}

type InboundKey =
  | "shrink"
  | "labelAttachment"
  | "modifyPackage"
  | "repackage"
  | "kitting";
type InboundItem = {
  name: InboundKey;
  label: string;
  info: string;
};

type QCKey =
  | "checkItems"
  | "checkDate"
  | "test"
  | "checkContents"
  | "requestedQc";
type QCItem = {
  name: QCKey;
  label: string;
  info?: string;
};

const initialItems = {
  id: 0,
  parcelId: 0,
  shrink: false,
  labelAttachment: false,
  modifyPackage: false,
  repackage: false,
  kitting: false,
  checkItems: false,
  checkDate: false,
  test: false,
  checkContents: false,
  requestedQc: false,
  inboundDescription: "",
  qcDescription: "",
};

const inboundItems: InboundItem[] = [
  {
    name: "shrink",
    label: "شیرینک",
    info: "در این سرویس تمامی کالا های موجود در محموله با استفاده از پلاستیک حرارت دیده روکش میشوند.",
  },
  {
    name: "labelAttachment",
    label: "برچسب‌گذاری",
    info: "لیبل و یا برچسب اختصاصی که میتواند شامل قیمت، لوگو و یا دیگر اطلاعات مورد نیاز شما باشد بر روی کالاها نصب میشود.",
  },
  {
    name: "modifyPackage",
    label: "اسمبلینگ",
    info: "در صورتی که کالای ارسال شده نیاز به اعمال تغییرات به صورت سطحی در ساختار دارند میتوانید از این سرویس استفاده نمایید.",
  },
  {
    name: "repackage",
    label: "تغییر بسته‌بندی",
    info: "در این سرویس کالاهای محموله میتواند در هنگام ورود بسته بندی شوند و یا تغغیراتی در بسته بندی کالا ها اعمال شود.",
  },
  {
    name: "kitting",
    label: "کیتینگ و باندلینگ",
    info: "کیتینگ به معنی ساخت یک پکیج فروش با استفاده از چندین کالا است که میتواند به صورت مجزا ارسال شده و تحت عنوان یک کالا جای گذاری شود.",
  },
];

const QCItems: QCItem[] = [
  {
    name: "checkItems",
    label: "هویت‌سنجی",
    info: "با انتخاب این گزینه تمامی کالاهای موجود در محموله به صورت تک به تک مورد بررسی قرارمیگیرند",
  },
  {
    name: "checkDate",
    label: "بررسی تاریخ تولید و انقضا",
  },
  {
    name: "test",
    label: "بررسی عملکرد کالا",
  },
  {
    name: "checkContents",
    label: "بررسی محتویات کارتن",
  },
  {
    name: "requestedQc",
    label: "کنترل کیفی درخواستی",
    info: "شما میتوانید با انتخاب این گزینه کنترل کیفی مورد نظر خود را شخصی سازی نمایید",
  },
];

const CustomizedItemsCard: FunctionComponent<CustomizedItemsCardProps> = ({
  parcel,
  userRole,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [customizedItems, setCustomizedItems] = useState(initialItems);

  useEffect(() => {
    if (parcel.parcelCustomizedOperationDTO) {
      setCustomizedItems(parcel.parcelCustomizedOperationDTO);
    }
  }, [parcel]);

  const handleCustomizedItemChange = (event: {
    target: { name: string | number; checked: any };
  }) => {
    const newItems = { ...customizedItems };
    newItems[event.target.name] = event.target.checked;
    setCustomizedItems(newItems);
  };

  const editModeToggle = () => {
    if (editMode) {
      updateCustomizedItems();
    } else {
      setEditMode(true);
    }
  };

  const updateCustomizedItems = async () => {
    try {
      await parcelsRepository.updateCustomizedOperation({
        payload: { body: { ...customizedItems }, id: parcel.id },
      });
      setEditMode(false);
      handleNotfication("محموله با موفقیت تغییر کرد", "success");
    } catch (error: any) {
      handleError(error);
    }
  };

  return (
    <Card
      style={{
        paddingTop: "0",
        marginRight: "24px",
        marginBottom: "24px",
        width: "100%",
      }}>
      <CardContent>
        <div className={classes.itemContainer}>
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>
            ورود کالا
          </span>
          <div className={classes.itemList}>
            {inboundItems.map((item) => (
              <>
                {editMode ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={handleCustomizedItemChange}
                        checked={customizedItems[item.name]}
                        name={item.name}
                        color="primary"
                        // disabled={values.inboundType == "VIP"}
                        size="small"
                      />
                    }
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: "10px",
                        }}>
                        <Typography
                          style={{
                            fontSize: "16px",
                            fontWeight: "400",
                            color: "rgba(47, 72, 88, 1)",
                          }}>
                          {item.label}
                        </Typography>
                      </div>
                    }
                  />
                ) : (
                  <span
                    style={{
                      color: customizedItems[item.name] ? "#313131" : "#D3D7E8",
                      lineHeight: "38px",
                    }}>
                    {item.label}
                  </span>
                )}
              </>
            ))}
          </div>
          {customizedItems?.inboundDescription && (
            <>
              <Divider style={{ width: "100%" }} />
              <p style={{ marginBottom: "0" }}>
                {customizedItems?.inboundDescription}
              </p>
            </>
          )}
        </div>
        <div className={classes.itemContainer} style={{ marginTop: "24px" }}>
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>
            کنترل کیفی
          </span>
          <div className={classes.itemList}>
            {QCItems.map((item) => (
              <>
                {editMode ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={handleCustomizedItemChange}
                        checked={customizedItems[item.name]}
                        name={item.name}
                        color="primary"
                        // disabled={values.inboundType == "VIP"}
                        size="small"
                      />
                    }
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: "10px",
                        }}>
                        <Typography
                          style={{
                            fontSize: "16px",
                            fontWeight: "400",
                            color: "rgba(47, 72, 88, 1)",
                          }}>
                          {item.label}
                        </Typography>
                      </div>
                    }
                  />
                ) : (
                  <span
                    style={{
                      color: customizedItems[item.name] ? "#313131" : "#D3D7E8",
                      lineHeight: "38px",
                    }}>
                    {item.label}
                  </span>
                )}
              </>
            ))}
          </div>
          {customizedItems?.qcDescription && (
            <>
              <Divider style={{ width: "100%" }} />
              <p style={{ marginBottom: "0" }}>
                {customizedItems?.qcDescription}
              </p>
            </>
          )}
        </div>
        {userRole == "WAREHOUSE_ADMIN" && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              style={{ marginTop: "24px", padding: "6px 16px" }}
              onClick={editModeToggle}>
              {editMode ? "تایید" : "ویرایش"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomizedItemsCard;
